import { useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { ROUTES } from '@/lib/routes';
import { getActiveDrillItemPath } from '../NavBar/nav-utils';
import { useNavSources } from '../NavBar/useNavSources';
import { buildConfigureNav } from './configure-nav';
import { rememberConfigureSection } from './configure-return';
import { useTakeoverEscape } from '../TakeoverShell';
import { ConfigureShellView } from './ConfigureShellView';

/**
 * Layout route for the Configure takeover: every organization configuration
 * page renders inside this shell (the app shell drops its sidebar on these
 * paths, see `isConfigurePath`). The nav is a single flat list of grouped
 * rows. `esc` and the Back chip share one leave handler that always returns
 * to the org's agent sessions page. Nested list↔detail uses
 * `NestedDetailHeader`, not this chip.
 *
 * While the user is inside Configure, the deepest matching nav row is
 * remembered as the per-org section so the next door open lands there.
 */
export function ConfigureShell() {
  const { configureDrillGroups } = useNavSources();
  const { activeMembership } = useAuth();
  const slug = useActiveOrgSlug();
  const navigate = useNavigate();
  const location = useLocation();

  const groups = useMemo(
    () => buildConfigureNav(slug, configureDrillGroups),
    [slug, configureDrillGroups],
  );

  const navItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // Remember the SECTION-level path (deepest matching nav row), never a
  // detail path. Paths no row claims (e.g. a brand-new plugin page mid-
  // restore) store nothing.
  useEffect(() => {
    const section = getActiveDrillItemPath(navItems, location.pathname);
    if (section && slug) rememberConfigureSection(slug, section);
  }, [navItems, location.pathname, slug]);

  const goBack = useCallback(() => {
    const fallback = slug ? ROUTES.org(slug).runs : ROUTES.root;
    void navigate(fallback);
  }, [navigate, slug]);

  useTakeoverEscape(goBack);

  return (
    <ConfigureShellView
      groups={groups}
      activePath={location.pathname}
      orgName={activeMembership?.organization.name ?? 'Organization'}
      orgAvatarUrl={activeMembership?.organization.avatarUrl}
      onBack={goBack}
    >
      <Outlet />
    </ConfigureShellView>
  );
}
